/**
 * versionReload — Umgang mit veralteten Programmteilen nach einem Deployment.
 *
 * Nach einem Deploy verweist ein bereits geöffneter Tab noch auf JS-Chunks des alten
 * Builds, die es nicht mehr gibt (ChunkLoadError). Statt einer Fehlerseite wird dann
 * gewartet, bis die neue Version ausgeliefert wird, und automatisch neu geladen.
 * Schutz vor Endlosschleifen: max. MAX_AUTO_RELOADS automatische Reloads je
 * WINDOW_MS; danach entscheidet der Benutzer per Button.
 */
const STORAGE_KEY = 'ew_version_reload';
const MAX_AUTO_RELOADS = 3;
const WINDOW_MS = 10 * 60 * 1000;
const POLL_MS = 3000;
const MAX_WAIT_MS = 3 * 60 * 1000;

export function isChunkLoadError(error) {
  if (!error) return false;
  const msg = String(error.message || error.reason?.message || error || '');
  return error.name === 'ChunkLoadError'
    || error.reason?.name === 'ChunkLoadError'
    || /Loading (CSS )?chunk [\w-]+ failed/i.test(msg)
    || msg.includes('Failed to fetch dynamically imported module')
    || msg.includes('Importing a module script failed');
}

function readState() {
  try {
    const s = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (s && Date.now() - s.first < WINDOW_MS) return s;
  } catch (_e) { /* ignore */ }
  return { count: 0, first: Date.now() };
}

function writeState(s) {
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch (_e) { /* ignore */ }
}

/** Darf noch automatisch neu geladen werden? */
export function canAutoReload() {
  return readState().count < MAX_AUTO_RELOADS;
}

/** Nach stabilem Laden aufrufen: Zähler zurücksetzen. */
export function markVersionLoaded() {
  try { sessionStorage.removeItem(STORAGE_KEY); } catch (_e) { /* ignore */ }
}

// Die neue Version gilt als bereit, sobald index.html wieder ausgeliefert wird
async function isAppReachable() {
  try {
    const controller = new AbortController();
    const t = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(`/index.html?_=${Date.now()}`, { cache: 'no-store', signal: controller.signal });
    clearTimeout(t);
    return res.ok;
  } catch (_e) {
    return false;
  }
}

let reloadPending = false;

/**
 * Wartet, bis die Anwendung wieder erreichbar ist, und lädt dann neu.
 * Gibt false zurück, wenn keine automatischen Reloads mehr erlaubt sind.
 */
export function reloadWhenReady() {
  if (reloadPending) return true;
  const s = readState();
  if (s.count >= MAX_AUTO_RELOADS) return false;
  reloadPending = true;
  writeState({ ...s, count: s.count + 1 });
  window.dispatchEvent(new CustomEvent('easywork:save-drafts', { detail: { reason: 'new-version' } }));
  const started = Date.now();
  const tick = async () => {
    if (await isAppReachable() || Date.now() - started > MAX_WAIT_MS) {
      window.location.reload();
      return;
    }
    setTimeout(tick, POLL_MS);
  };
  tick();
  return true;
}

/** Globale Listener für Chunk-Fehler außerhalb von React-Renderpfaden (z. B. import() in Klick-Handlern). */
export function installChunkErrorHandlers() {
  const handle = (err) => {
    if (!isChunkLoadError(err)) return;
    if (reloadWhenReady()) window.dispatchEvent(new CustomEvent('easywork:new-version'));
  };
  window.addEventListener('unhandledrejection', (e) => handle(e.reason));
  window.addEventListener('error', (e) => handle(e.error || e.message));
  // Nach 60 s ohne Chunk-Fehler gilt die geladene Version als stabil
  setTimeout(markVersionLoaded, 60 * 1000);
}
